import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloudIcon from '@mui/icons-material/Cloud';

// Import S3 service
import s3Service, { 
  uploadToS3, 
  deleteFromS3, 
  formatS3MediaItem,
  getPublicUrl,
  listFilesFromS3 
} from '../services/s3Service';

const Media = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);
  
  // Load media items from S3
  useEffect(() => {
    const fetchMediaFromS3 = async () => {
      try {
        setIsLoading(true);
        const s3Objects = await listFilesFromS3();
        
        if (s3Objects && s3Objects.length > 0) {
          const formattedItems = s3Objects.map((item, index) => ({
            id: Date.now() + index,
            title: item.key.split('/').pop(),
            url: item.url,
            type: 'image',
            uploadDate: new Date(item.lastModified).toISOString().split('T')[0],
            size: `${Math.round(item.size / 1024)} KB`,
            s3Key: item.key
          }));
          
          setMediaItems(formattedItems);
        }
      } catch (error) {
        console.error('Error fetching S3 media:', error);
        setSnackbarMessage('Error loading media from S3. Please try again later.');
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMediaFromS3();
  }, []);
  
  // Filter media items based on search term and current tab
  const filteredMedia = mediaItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
    if (currentTab === 0) return matchesSearch; // All
    if (currentTab === 1) return matchesSearch && item.type === 'image'; // Images
    return matchesSearch;
  });
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  // Confirm delete dialog
  const handleDeleteClick = (media) => {
    setSelectedMedia(media);
    setOpenDialog(true);
  };
  
  // Delete media item
  const handleDeleteConfirm = async () => {
    try {
      // If it's an S3 item with a key, delete from S3 too
      if (selectedMedia.s3Key) {
        await deleteFromS3(selectedMedia.s3Key);
      }
      
      // Remove from state
      setMediaItems(mediaItems.filter(item => item.id !== selectedMedia.id));
      setOpenDialog(false);
      setSnackbarMessage('Media deleted successfully');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Error deleting media:', error);
      setSnackbarMessage('Error deleting media. Please try again.');
      setOpenSnackbar(true);
    }
  };
  
  // Copy URL to clipboard
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setSnackbarMessage('URL copied to clipboard');
    setOpenSnackbar(true);
  };
  
  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadTitle) {
        // Use filename as default title (without extension)
        const fileName = file.name.split('.').slice(0, -1).join('.');
        setUploadTitle(fileName);
      }
    }
  };
  
  // Handle file drop
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      setSelectedFile(file);
      if (!uploadTitle) {
        const fileName = file.name.split('.').slice(0, -1).join('.');
        setUploadTitle(fileName);
      }
    }
  };
  
  // Prevent default for drag over
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  
  // Handle browse files click
  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Upload new media
  const handleUpload = async () => {
    if (!selectedFile) {
      setSnackbarMessage('Please select a file to upload');
      setOpenSnackbar(true);
      return;
    }
    
    setIsUploading(true);
    setUploadError('');
    
    try {
      // Generate a custom key with folder structure
      const fileExt = selectedFile.name.split('.').pop();
      const customKey = `uploads/${Date.now()}-${uploadTitle.replace(/\s+/g, '-').toLowerCase()}.${fileExt}`;
      
      // Upload to S3
      const s3Result = await uploadToS3(selectedFile, customKey);
      
      // Create new media item from S3 result
      const newItem = {
        id: Date.now(),
        title: uploadTitle || 'New Upload',
        url: s3Result.url,
        type: 'image',
        uploadDate: new Date().toISOString().split('T')[0],
        size: `${Math.round(selectedFile.size / 1024)} KB`,
        s3Key: s3Result.key
      };
      
      // Update state with new media item
      const updatedItems = [newItem, ...mediaItems];
      setMediaItems(updatedItems);
      
      setUploadDialog(false);
      setSnackbarMessage('Media uploaded successfully to S3');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Error uploading file. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset state on success
      if (!uploadError) {
        setSelectedFile(null);
        setUploadTitle('');
      }
    }
  };

  // Close upload dialog
  const handleCloseUploadDialog = () => {
    setUploadDialog(false);
    setSelectedFile(null);
    setUploadTitle('');
    setUploadError('');
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Media Library
        </Typography>
        <Box>
          <Button 
            variant="contained" 
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => setUploadDialog(true)}
          >
            Upload New Media
          </Button>
        </Box>
      </Box>
      
      <Paper sx={{ mb: 4, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search media..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="All" />
              <Tab label="Images" />
            </Tabs>
          </Grid>
        </Grid>
      </Paper>
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredMedia.map((media) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={media.id}>
              <Card sx={{ height: '100%', borderRadius: 2, overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  height="160"
                  image={media.url}
                  alt={media.title}
                  sx={{
                    objectFit: media.isUploaded ? 'contain' : 'cover',
                    bgcolor: media.isUploaded ? '#f5f5f5' : 'transparent'
                  }}
                />
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    {media.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {media.uploadDate} • {media.size}
                    {media.s3Key && (
                      <Box component="span" sx={{ ml: 1 }}>
                        • <CloudIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                      </Box>
                    )}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button 
                    size="small" 
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopyUrl(media.url)}
                  >
                    Copy URL
                  </Button>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleDeleteClick(media)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
          
          {filteredMedia.length === 0 && !isLoading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  No media items found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search or upload new media
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedMedia?.title}"? This action cannot be undone.
            {selectedMedia?.s3Key && (
              <Typography color="error" sx={{ mt: 1 }}>
                This will also delete the file from AWS S3 storage.
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      
      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload New Media to AWS S3
        </DialogTitle>
        <DialogContent>
          <Box 
            sx={{ 
              border: '2px dashed #ccc', 
              borderRadius: 2, 
              p: 4, 
              textAlign: 'center',
              my: 2,
              cursor: 'pointer',
              bgcolor: selectedFile ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleBrowseClick}
          >
            {selectedFile ? (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  File selected:
                </Typography>
                <Typography variant="body1" color="primary" sx={{ mb: 2 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(selectedFile.size / 1024)} KB
                </Typography>
              </>
            ) : (
              <>
                <FileUploadIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6">
                  Drag and drop files here
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                  or
                </Typography>
                <Button variant="contained">Browse Files</Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Maximum file size: 10MB
                </Typography>
              </>
            )}
            <input 
              type="file" 
              hidden 
              ref={fileInputRef} 
              onChange={handleFileSelect}
              accept="image/*"
            />
          </Box>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            placeholder="Enter a title for this media"
            value={uploadTitle}
            onChange={(e) => setUploadTitle(e.target.value)}
          />
          
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            This file will be uploaded to AWS S3 bucket: dozer-web-250503
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancel</Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            disabled={!selectedFile || isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity="success" 
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Media;